import React, { useState, useEffect, useCallback } from 'react';
import {
  Search,
  X,
  Plus,
  Paperclip,
  Smile,
  Send,
  ChevronDown,
  Loader2,
  RefreshCw,
  Users,
} from 'lucide-react';
import { messagesApi } from '../../../lib/api';
import { useAuth } from '../../../app/providers/AuthProvider';

interface Contact {
  id: string;
  name: string;
  avatar: string | null;
  isOnline?: boolean;
}

interface Message {
  id: string;
  content: string;
  attachments: any[];
  is_edited: boolean;
  is_deleted: boolean;
  created_at: string;
  sender: {
    id: string;
    name: string;
    avatar: string | null;
  };
  reply_to?: {
    id: string;
    content: string;
    sender_name: string;
  };
}

interface Conversation {
  id: string;
  type: 'direct' | 'group';
  name: string | null;
  last_message_at: string | null;
  last_message_preview: string | null;
  unread_count: number;
  is_muted: boolean;
  recipient?: Contact;
  participants?: Array<Contact & { is_admin: boolean }>;
}

// Fallback mock data
const fallbackConversations: Conversation[] = [
  {
    id: 'mock-1',
    type: 'direct',
    name: null,
    last_message_at: new Date().toISOString(),
    last_message_preview: 'Hey, we are still on for today? I want...',
    unread_count: 0,
    is_muted: false,
    recipient: { id: '1', name: 'John Doe', avatar: null },
  },
  {
    id: 'mock-2',
    type: 'direct',
    name: null,
    last_message_at: new Date(Date.now() - 86400000).toISOString(),
    last_message_preview: 'Thanks for the help!',
    unread_count: 2,
    is_muted: false,
    recipient: { id: '2', name: 'Jane Smith', avatar: null },
  },
];

export function MessagesPage() {
  const { currentSchool } = useAuth();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [messageInput, setMessageInput] = useState('');
  const [showNewMessageModal, setShowNewMessageModal] = useState(false);
  const [recipientSearch, setRecipientSearch] = useState('');
  const [searchResults, setSearchResults] = useState<Contact[]>([]);
  const [selectedRecipients, setSelectedRecipients] = useState<Contact[]>([]);
  const [newMessageInput, setNewMessageInput] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string>('');

  // Get current user ID from auth
  useEffect(() => {
    const authData = localStorage.getItem('auth_user');
    if (authData) {
      try {
        const user = JSON.parse(authData);
        setCurrentUserId(user.public_id || user.id);
      } catch (e) {
        console.error('Failed to parse auth user:', e);
      }
    }
  }, []);

  // Fetch conversations
  const fetchConversations = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await messagesApi.getConversations();
      setConversations(response.conversations);
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
      // Use fallback data
      setConversations(fallbackConversations);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  // Fetch messages when conversation is selected
  const fetchMessages = useCallback(async (conversationId: string) => {
    setIsLoadingMessages(true);
    try {
      const response = await messagesApi.getConversation(conversationId);
      setMessages(response.messages.reverse()); // Reverse to show oldest first
    } catch (error) {
      console.error('Failed to fetch messages:', error);
      setMessages([]);
    } finally {
      setIsLoadingMessages(false);
    }
  }, []);

  // Search users for new message
  const searchUsers = useCallback(async (query: string) => {
    if (!currentSchool?.id || query.length < 2) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const response = await messagesApi.searchUsers(query, currentSchool.id);
      setSearchResults(response.users);
    } catch (error) {
      console.error('Failed to search users:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, [currentSchool?.id]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (recipientSearch.length >= 2) {
        searchUsers(recipientSearch);
      } else {
        setSearchResults([]);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [recipientSearch, searchUsers]);

  const handleSelectConversation = (conversation: Conversation) => {
    setSelectedConversation(conversation);
    fetchMessages(conversation.id);

    // Mark as read
    if (conversation.unread_count > 0) {
      messagesApi.markAsRead(conversation.id).catch(console.error);
      setConversations(prev =>
        prev.map(c =>
          c.id === conversation.id ? { ...c, unread_count: 0 } : c
        )
      );
    }
  };

  const handleSendMessage = async () => {
    if (!messageInput.trim() || !selectedConversation || isSending) return;

    const content = messageInput.trim();
    setMessageInput('');
    setIsSending(true);

    try {
      const response = await messagesApi.sendMessage(selectedConversation.id, { content });
      setMessages(prev => [...prev, response.message]);

      // Update conversation preview
      setConversations(prev =>
        prev.map(c =>
          c.id === selectedConversation.id
            ? { ...c, last_message_preview: content, last_message_at: new Date().toISOString() }
            : c
        )
      );
    } catch (error) {
      console.error('Failed to send message:', error);
      setMessageInput(content); // Restore message on error
    } finally {
      setIsSending(false);
    }
  };

  const handleAddRecipient = (contact: Contact) => {
    setSelectedRecipients([...selectedRecipients, contact]);
    setRecipientSearch('');
    setSearchResults([]);
  };

  const handleRemoveRecipient = (contactId: string) => {
    setSelectedRecipients(selectedRecipients.filter((r) => r.id !== contactId));
  };

  const handleStartNewConversation = async () => {
    if (selectedRecipients.length === 0 || !newMessageInput.trim() || !currentSchool?.id) return;

    setIsSending(true);
    try {
      if (selectedRecipients.length === 1) {
        // Start direct conversation
        const response = await messagesApi.startDirect({
          recipient_id: selectedRecipients[0].id,
          school_id: currentSchool.id,
          message: newMessageInput.trim(),
        });

        // Add new conversation and select it
        setConversations(prev => [response.conversation, ...prev]);
        setSelectedConversation(response.conversation);
        setMessages([response.message]);
      } else {
        // Create group conversation
        const response = await messagesApi.createGroup({
          name: `Group (${selectedRecipients.length + 1})`,
          participant_ids: selectedRecipients.map(r => r.id),
          school_id: currentSchool.id,
        });

        // Send first message
        const msgResponse = await messagesApi.sendMessage(response.conversation.id, {
          content: newMessageInput.trim(),
        });

        setConversations(prev => [response.conversation, ...prev]);
        setSelectedConversation(response.conversation);
        setMessages([msgResponse.message]);
      }

      setShowNewMessageModal(false);
      setSelectedRecipients([]);
      setNewMessageInput('');
    } catch (error) {
      console.error('Failed to start conversation:', error);
    } finally {
      setIsSending(false);
    }
  };

  const formatTime = (dateString: string | null) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - date.getTime()) / 86400000);

    if (diffDays === 0) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return date.toLocaleDateString([], { weekday: 'short' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  const filteredConversations = conversations.filter((conv) => {
    const searchName = conv.type === 'direct'
      ? conv.recipient?.name || ''
      : conv.name || '';
    return searchName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (conv.last_message_preview || '').toLowerCase().includes(searchQuery.toLowerCase());
  });

  const getConversationName = (conv: Conversation) => {
    if (conv.type === 'direct') {
      return conv.recipient?.name || 'Unknown';
    }
    return conv.name || 'Group Chat';
  };

  const getConversationAvatar = (conv: Conversation) => {
    if (conv.type === 'direct') {
      return conv.recipient?.name?.charAt(0) || '?';
    }
    return <Users className="w-5 h-5" />;
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-6 border-b border-gray-200 flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Messages Center</h1>
        <button
          onClick={fetchConversations}
          className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
          disabled={isLoading}
        >
          <RefreshCw className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Conversations List */}
        <div className="w-80 border-r border-gray-200 flex flex-col bg-white">
          {/* List Header */}
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <button
                onClick={() => setShowNewMessageModal(true)}
                className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <Plus className="w-4 h-4" />
                New Message
              </button>
              <button className="p-1 text-gray-400 hover:text-gray-600">
                <ChevronDown className="w-4 h-4" />
              </button>
            </div>

            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search Messages"
                className="w-full pl-10 pr-4 py-2 bg-gray-100 border-0 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
          </div>

          {/* Conversations */}
          <div className="flex-1 overflow-y-auto">
            {isLoading ? (
              <div className="p-8 text-center">
                <Loader2 className="w-6 h-6 animate-spin mx-auto text-teal-500" />
                <p className="text-sm text-gray-500 mt-2">Loading conversations...</p>
              </div>
            ) : (
              <>
                {filteredConversations.map((conversation) => (
                  <button
                    key={conversation.id}
                    onClick={() => handleSelectConversation(conversation)}
                    className={`w-full flex items-start gap-3 p-4 hover:bg-gray-50 transition-colors border-b border-gray-100 ${
                      selectedConversation?.id === conversation.id ? 'bg-teal-50' : ''
                    }`}
                  >
                    {/* Avatar */}
                    <div className="relative">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center text-white font-medium">
                        {getConversationAvatar(conversation)}
                      </div>
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0 text-left">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-gray-900">{getConversationName(conversation)}</span>
                        <span className="text-xs text-gray-500">{formatTime(conversation.last_message_at)}</span>
                      </div>
                      <p className="text-sm text-gray-500 truncate">{conversation.last_message_preview || 'No messages yet'}</p>
                    </div>

                    {/* Unread Badge */}
                    {conversation.unread_count > 0 && (
                      <div className="w-5 h-5 rounded-full bg-teal-500 flex items-center justify-center">
                        <span className="text-xs text-white font-medium">{conversation.unread_count}</span>
                      </div>
                    )}
                  </button>
                ))}

                {filteredConversations.length === 0 && (
                  <div className="p-8 text-center text-gray-500">
                    {searchQuery ? 'No conversations found' : 'No conversations yet'}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Chat Area */}
        <div className="flex-1 flex flex-col bg-gray-50">
          {selectedConversation ? (
            <>
              {/* Chat Header */}
              <div className="flex items-center justify-between p-4 bg-white border-b border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center text-white font-medium">
                      {getConversationAvatar(selectedConversation)}
                    </div>
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">{getConversationName(selectedConversation)}</h3>
                    <p className="text-xs text-gray-500">
                      {selectedConversation.type === 'group'
                        ? `${selectedConversation.participants?.length || 0} members`
                        : 'Direct message'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedConversation(null)}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {isLoadingMessages ? (
                  <div className="flex items-center justify-center h-full">
                    <Loader2 className="w-6 h-6 animate-spin text-teal-500" />
                  </div>
                ) : (
                  <>
                    {messages.map((message) => {
                      const isMe = message.sender.id === currentUserId;
                      return (
                        <div
                          key={message.id}
                          className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}
                        >
                          {!isMe && (
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center text-white text-sm font-medium mr-2">
                              {message.sender.name.charAt(0)}
                            </div>
                          )}
                          <div className="max-w-xs">
                            {!isMe && selectedConversation.type === 'group' && (
                              <p className="text-xs text-gray-500 mb-1 ml-1">{message.sender.name}</p>
                            )}
                            <div
                              className={`px-4 py-2 rounded-2xl ${
                                isMe
                                  ? 'bg-teal-500 text-white rounded-br-sm'
                                  : 'bg-white text-gray-900 rounded-bl-sm shadow-sm'
                              }`}
                            >
                              {message.reply_to && (
                                <div className={`text-xs mb-1 ${isMe ? 'text-teal-100' : 'text-gray-400'}`}>
                                  Replying to {message.reply_to.sender_name}
                                </div>
                              )}
                              <p className="text-sm whitespace-pre-line">
                                {message.is_deleted ? 'This message was deleted' : message.content}
                              </p>
                              {message.is_edited && (
                                <span className={`text-xs ${isMe ? 'text-teal-100' : 'text-gray-400'}`}> (edited)</span>
                              )}
                            </div>
                            <p className={`text-xs text-gray-400 mt-1 ${isMe ? 'text-right' : ''}`}>
                              {new Date(message.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </p>
                          </div>
                        </div>
                      );
                    })}

                    {messages.length === 0 && (
                      <div className="flex items-center justify-center h-full text-gray-500">
                        No messages yet. Start the conversation!
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Input Area */}
              <div className="p-4 bg-white border-t border-gray-200">
                <div className="flex items-center gap-2">
                  <button className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg">
                    <Paperclip className="w-5 h-5" />
                  </button>
                  <input
                    type="text"
                    value={messageInput}
                    onChange={(e) => setMessageInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSendMessage()}
                    placeholder="Type a message..."
                    disabled={isSending}
                    className="flex-1 px-4 py-2 bg-gray-100 border-0 rounded-full focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:opacity-50"
                  />
                  <button className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg">
                    <Smile className="w-5 h-5" />
                  </button>
                  <button
                    onClick={handleSendMessage}
                    disabled={!messageInput.trim() || isSending}
                    className="p-2 text-teal-600 hover:text-teal-700 hover:bg-teal-50 rounded-lg disabled:opacity-50"
                  >
                    {isSending ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <Send className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-gray-500">
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                  <Send className="w-8 h-8 text-gray-400" />
                </div>
                <p>Select a conversation to start messaging</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* New Message Modal */}
      {showNewMessageModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl w-full max-w-md shadow-xl">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">New Message</h3>
              <button
                onClick={() => {
                  setShowNewMessageModal(false);
                  setSelectedRecipients([]);
                  setRecipientSearch('');
                  setSearchResults([]);
                }}
                className="p-1 text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Recipients */}
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-gray-500">To</span>
                {selectedRecipients.map((recipient) => (
                  <div
                    key={recipient.id}
                    className="flex items-center gap-1 px-2 py-1 bg-teal-100 text-teal-700 rounded-full text-sm"
                  >
                    <div className="w-5 h-5 rounded-full bg-teal-500 flex items-center justify-center text-white text-xs">
                      {recipient.name.charAt(0)}
                    </div>
                    <span>{recipient.name}</span>
                    <button
                      onClick={() => handleRemoveRecipient(recipient.id)}
                      className="ml-1 text-teal-600 hover:text-teal-800"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
                <input
                  type="text"
                  value={recipientSearch}
                  onChange={(e) => setRecipientSearch(e.target.value)}
                  placeholder={selectedRecipients.length === 0 ? 'Search by name' : ''}
                  className="flex-1 min-w-[100px] py-1 border-0 focus:outline-none focus:ring-0 text-sm"
                />
              </div>

              {/* Search Results Dropdown */}
              {(isSearching || searchResults.length > 0) && recipientSearch.length >= 2 && (
                <div className="mt-2 border border-gray-200 rounded-lg overflow-hidden max-h-48 overflow-y-auto">
                  {isSearching ? (
                    <div className="p-3 text-center">
                      <Loader2 className="w-4 h-4 animate-spin mx-auto text-teal-500" />
                    </div>
                  ) : searchResults.length > 0 ? (
                    searchResults
                      .filter(c => !selectedRecipients.find(r => r.id === c.id))
                      .map((contact) => (
                        <button
                          key={contact.id}
                          onClick={() => handleAddRecipient(contact)}
                          className="w-full flex items-center gap-3 p-3 hover:bg-gray-50 transition-colors"
                        >
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center text-white text-sm">
                            {contact.name.charAt(0)}
                          </div>
                          <span className="text-sm text-gray-900">{contact.name}</span>
                        </button>
                      ))
                  ) : (
                    <div className="p-3 text-sm text-gray-500 text-center">
                      No users found
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Message Input */}
            <div className="p-4">
              <div className="flex items-center gap-2">
                <button className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg">
                  <Paperclip className="w-5 h-5" />
                </button>
                <input
                  type="text"
                  value={newMessageInput}
                  onChange={(e) => setNewMessageInput(e.target.value)}
                  placeholder="Type a message..."
                  className="flex-1 px-4 py-2 bg-gray-100 border-0 rounded-full focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
                <button className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg">
                  <Smile className="w-5 h-5" />
                </button>
                <button
                  onClick={handleStartNewConversation}
                  disabled={selectedRecipients.length === 0 || !newMessageInput.trim() || isSending}
                  className="p-2 text-teal-600 hover:text-teal-700 hover:bg-teal-50 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSending ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Send className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
